import { mixColor, type SiteThemeId } from './siteTheme';
import type { BackgroundConfig } from './siteBackgroundSettings';
import type { VfxTier } from '@/utils/vfxManager';

type Point = { x: number; y: number };
export type NeuronFiber = { points: Point[]; width: number; phase: number };
export type NeuronCell = { x: number; y: number; layer: number; phase: number; radius: number; fibers: NeuronFiber[] };
export type NeuronPose = { x: number; y: number; scroll: number };
export const NEURON_REST: NeuronPose = { x: 0, y: 0, scroll: 0 };
const DEPTHS = [-.85, 0, .8];
const PAD = 120;

/** Стабильная анатомия: плавные кривые разной длины, не радиальная сетка из прямых рёбер. */
export function createNeuronScene(count: number): NeuronCell[] {
  let seed = 2731;
  const random = () => { seed = seed * 16807 % 2147483647; return (seed - 1) / 2147483646; };
  const anchors = [[.26, .56], [.76, .77], [.87, .17], [.08, .13], [.68, .12], [.48, .93]];
  return Array.from({ length: count }, (_, index) => {
    const layer = [1, 2, 0, 0, 1, 0][index % 6];
    const [x, y] = anchors[index] || [((index * .61803398875) % 1) * 1.32 - .16, ((index * .41421356237) % 1) * 1.3 - .15];
    const phase = random() * Math.PI * 2;
    const fibers: NeuronFiber[] = [];
    const grow = (start: Point, angle: number, length: number, thickness: number, fork: boolean) => {
      const bend = (random() - .5) * 1.9;
      const controls = [start,
        { x: start.x + Math.cos(angle + bend) * length * .4, y: start.y + Math.sin(angle + bend) * length * .4 },
        { x: start.x + Math.cos(angle - bend * .6) * length * .8, y: start.y + Math.sin(angle - bend * .6) * length * .8 },
        { x: start.x + Math.cos(angle + bend * .2) * length, y: start.y + Math.sin(angle + bend * .2) * length }];
      const points = Array.from({ length: 33 }, (_, i) => {
        const t = i / 32; const u = 1 - t;
        return {
          x: u ** 3 * controls[0].x + 3 * u * u * t * controls[1].x + 3 * u * t * t * controls[2].x + t ** 3 * controls[3].x,
          y: u ** 3 * controls[0].y + 3 * u * u * t * controls[1].y + 3 * u * t * t * controls[2].y + t ** 3 * controls[3].y,
        };
      });
      fibers.push({ points, width: thickness, phase: random() });
      if (fork) {
        grow(points[20], angle + (random() > .5 ? .6 : -.6), length * .62, thickness * .52, false);
        grow(points[32], angle + bend * .35, length * .7, thickness * .55, false);
      }
    };
    const arms = 5 + Math.floor(random() * 4);
    for (let arm = 0; arm < arms; arm++) {
      const angle = phase + arm / arms * Math.PI * 2 + (random() - .5) * .45;
      grow({ x: 0, y: 0 }, angle, .19 + random() * .31, .65 + random() * .85, arm % 2 === 0);
    }
    grow({ x: 0, y: 0 }, phase + 2.3, .65 + random() * .3, .85, true);
    return { x, y, layer, phase, radius: 3.8 + random() * 3.5, fibers };
  });
}

export function neuronDensity(config: BackgroundConfig, width: number, tier: VfxTier) {
  return Math.max(6, Math.min(config.count, width <= 768 ? 12 : tier === 'max' ? 36 : 18));
}

export function neuronLayerTransform(layer: number, pose: NeuronPose, depth: number, time: number) {
  const z = DEPTHS[layer] * depth / 100;
  return {
    x: pose.x * (26 + z * 72) + Math.sin(time / 17000 + layer * 2) * z * 7,
    y: pose.y * (14 + z * 42) - pose.scroll * z * 85 + Math.sin(time / 23000 + layer) * z * 5,
    angle: pose.x * z * .045 + pose.scroll * z * .055,
  };
}

export function sampleNeuronFiber(points: Point[], progress: number): Point {
  const at = Math.max(0, Math.min(1, progress)) * (points.length - 1);
  const i = Math.min(points.length - 2, Math.floor(at));
  const t = at - i;
  return { x: points[i].x + (points[i + 1].x - points[i].x) * t, y: points[i].y + (points[i + 1].y - points[i].y) * t };
}

type PreparedCell = NeuronCell & { cx: number; cy: number; scale: number };
export type PreparedNeuronField = {
  width: number; height: number; depth: number; light: boolean; background: string;
  cells: PreparedCell[]; layers: HTMLCanvasElement[]; sparks: HTMLCanvasElement[]; cores: HTMLCanvasElement[];
};

function makeCanvas(doc: Document, width: number, height: number) {
  const canvas = doc.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width)); canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

function glowSprite(doc: Document, color: string, core: boolean) {
  const canvas = makeCanvas(doc, 96, 96);
  const ctx = canvas.getContext('2d')!;
  const glow = ctx.createRadialGradient(48, 48, 0, 48, 48, 46);
  glow.addColorStop(0, core ? '#FFF7EB' : '#FFE8EF');
  glow.addColorStop(core ? .08 : .025, color);
  glow.addColorStop(.22, color + '88');
  glow.addColorStop(.55, color + '18');
  glow.addColorStop(1, color + '00');
  ctx.fillStyle = glow; ctx.fillRect(0, 0, 96, 96);
  return canvas;
}

/** Дорогие штрихи, свечение и дефокус рисуются только при смене геометрии/настроек. */
export function prepareNeuronField(doc: Document, width: number, height: number, config: BackgroundConfig, theme: SiteThemeId, background: string, tier: VfxTier, preview = false): PreparedNeuronField {
  const colors = config.colors[theme];
  const light = theme === 'light';
  const depth = config.depth / 100;
  const scene = createNeuronScene(preview ? config.count : neuronDensity(config, width, tier));
  const cells = scene.map(cell => ({ ...cell, cx: cell.x * width, cy: cell.y * height,
    scale: Math.min(width, height) * (1 + DEPTHS[cell.layer] * depth * .32) }));
  const rasterScale = Math.min(1.5, doc.defaultView?.devicePixelRatio || 1, Math.sqrt(8_000_000 / (3 * (width + PAD * 2) * (height + PAD * 2))));
  const layers = DEPTHS.map((z, layer) => {
    const canvas = makeCanvas(doc, (width + PAD * 2) * rasterScale, (height + PAD * 2) * rasterScale);
    const ctx = canvas.getContext('2d')!;
    ctx.scale(rasterScale, rasterScale); ctx.translate(PAD, PAD);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    const color = mixColor(colors.fiber, background, layer === 0 ? .42 : .06);
    const alpha = light ? [ .18, .48, .16 ][layer] : [ .45, .7, .38 ][layer];
    for (const cell of cells.filter(c => c.layer === layer)) {
      for (const fiber of cell.fibers) {
        ctx.beginPath();
        fiber.points.forEach((p, i) => {
          const x = cell.cx + p.x * cell.scale; const y = cell.cy + p.y * cell.scale;
          if (!i) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        });
        ctx.strokeStyle = color; ctx.globalAlpha = alpha;
        ctx.lineWidth = fiber.width * (1 + Math.max(0, z) * depth * 3.4);
        ctx.shadowColor = color; ctx.shadowBlur = light ? 0 : 3 + Math.max(0, z) * 7;
        ctx.stroke();
        if (layer === 1) {
          ctx.strokeStyle = mixColor(colors.fiber, '#D5EDFF', .35);
          ctx.lineWidth = fiber.width * .38; ctx.globalAlpha = light ? .25 : .5; ctx.shadowBlur = 0; ctx.stroke();
        }
      }
      ctx.shadowBlur = 0; ctx.globalAlpha = light ? .25 : .7;
      ctx.fillStyle = colors.signal; ctx.beginPath();
      for (let i = 0; i <= 16; i++) {
        const a = i / 16 * Math.PI * 2;
        const r = cell.radius * (1 + .16 * Math.sin(a * 5 + cell.phase));
        const x = cell.cx + Math.cos(a) * r; const y = cell.cy + Math.sin(a) * r * .82;
        if (!i) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.fill();
    }
    const blur = Math.abs(z) * config.blur / 100 * depth * (layer === 2 ? 11 : 4);
    if (blur < .2) return canvas;
    const softened = makeCanvas(doc, canvas.width, canvas.height);
    const soft = softened.getContext('2d')!;
    soft.filter = `blur(${(blur * rasterScale).toFixed(2)}px)`;
    soft.drawImage(canvas, 0, 0); canvas.width = canvas.height = 1;
    return softened;
  });
  return { width, height, depth: config.depth, light,
    background: mixColor(background, colors.haze, light ? .12 : .42), cells, layers,
    sparks: DEPTHS.map(() => glowSprite(doc, colors.signal, false)),
    cores: DEPTHS.map(() => glowSprite(doc, colors.signal, true)) };
}

export function paintNeuronFrame(ctx: CanvasRenderingContext2D, field: PreparedNeuronField, pose: NeuronPose, time: number, excited: number | null) {
  const { width, height, light } = field;
  ctx.globalAlpha = 1; ctx.fillStyle = field.background; ctx.fillRect(0, 0, width, height);
  for (let layer = 0; layer < 3; layer++) {
    const transform = neuronLayerTransform(layer, pose, field.depth, time);
    ctx.save(); ctx.translate(width / 2 + transform.x, height / 2 + transform.y); ctx.rotate(transform.angle); ctx.translate(-width / 2, -height / 2);
    ctx.drawImage(field.layers[layer], -PAD, -PAD, width + PAD * 2, height + PAD * 2);
    field.cells.forEach((cell, index) => {
      if (cell.layer !== layer) return;
      const breath = .8 + Math.sin(time / 3800 + cell.phase) * .15;
      const activation = index === excited ? 1.5 : 1;
      const radius = cell.radius * (layer === 2 ? 9 : layer === 0 ? 4.5 : 6.5);
      ctx.globalAlpha = (light ? .22 : layer === 0 ? .4 : .85) * breath * activation;
      ctx.drawImage(field.cores[layer], cell.cx - radius, cell.cy - radius, radius * 2, radius * 2);
      // Движение отдельных импульсов по волокнам, без общего стробирования сцены.
      cell.fibers.forEach((fiber, i) => {
        if (i % 3 !== 0) return;
        const progress = (time / (12500 + i * 730) + fiber.phase) % 1;
        for (let tail = 0; tail < 3; tail++) {
          const p = sampleNeuronFiber(fiber.points, (progress - tail * .019 + 1) % 1);
          const x = cell.cx + p.x * cell.scale; const y = cell.cy + p.y * cell.scale;
          const size = (layer === 2 ? 13 : layer === 0 ? 6 : 5) * (tail ? .72 : 1);
          ctx.globalAlpha = (light ? .35 : layer === 0 ? .3 : .8) * (1 - tail * .3) * activation;
          ctx.drawImage(field.sparks[layer], x - size, y - size, size * 2, size * 2);
        }
      });
    });
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

export function disposeNeuronField(field: PreparedNeuronField) {
  for (const canvas of [...field.layers, ...field.sparks, ...field.cores]) canvas.width = canvas.height = 1;
}
